import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { gameCenterLimits } from "configs/limits";
import { usePermissions } from "helpers/usePermission";
import { useAppDispatch, useAppSelector } from "store";
import { openModal } from "store/modals/slice";
import { ModalTypes } from "configs/types";
import {
  deleteMultiScopeItem,
  getMultiScopeList,
  handlePaginate,
  handleSelectAll,
  handleSelectItem,
} from "store/gameCenter/multiScope/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";

import ActionBtn from "components/buttons/ActionBtn";
import { RefreshButton } from "components/buttons/RefreshButton";
import ToggleBtn from "components/buttons/ToggleBtn";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import Pagination from "components/Pagination";
import { Table } from "components/table/Table";
import { GuideButton } from "modals/guides/GuideButton";
import { useGuideModal } from "modals/guides/useGuideModal";
import GameCenterFilters from "../GameCenterFilters";
import NoImage from "assets/images/image_not_found.webp";
import editIcon from "assets/icons/edit-icon.svg";
import listIcon from "assets/icons/list-icon.svg";
import gridIcon from "assets/icons/grid-icon.svg";

const MultiScopeList: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { openGuide } = useGuideModal();
  const permissions = usePermissions("game_center_multi_scope");
  const [limit, setLimit] = useState(gameCenterLimits[0]);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState<{ value: number | string; label: string }[]>([]);
  const [activeView, setActiveView] = useState("table");

  const { list, isListFetched, totalItems, totalPages, activePage, selectedItems, processingItemId } =
    useAppSelector((state) => state.multiScope);

  useEffect(() => {
    if (!permissions?.includes("read") && permissions !== null) navigate("/dashboard");

    const savedView = localStorage.getItem("grid");
    if (savedView) setActiveView(savedView);
  }, [navigate, permissions]);

  useEffect(() => {
    if (!isListFetched) {
      dispatch(getMultiScopeList({
        limit,
        offset: activePage - 1,
        title: search || undefined,
        isActive: active[0] && active[0].value !== 3 ? Number(active[0].value) : undefined,
      }));
    }
  }, [activePage, active, dispatch, isListFetched, limit, search]);

  const applyFilters = () => dispatch(handlePaginate(1));
  const openCreateModal = () => {
    navigate(`${location.pathname}?activeModal=${ModalTypes.multiScope}`);
    dispatch(openModal({ activeModal: ModalTypes.multiScope }));
  };
  const openEditModal = (id: number) => {
    navigate(`${location.pathname}?activeModal=${ModalTypes.multiScope}&entityId=${id}`);
    dispatch(openModal({ activeModal: ModalTypes.multiScope }));
  };
  const deleteItem = async (id: number) => {
    try {
      await dispatch(deleteMultiScopeItem(id)).unwrap();
      dispatch(showNotification({ title: t("success"), message: t("entityDeleteSuccess"), type: NotificationTypes.success }));
    } catch {
      dispatch(showNotification({ title: t("error"), message: t("entityDeleteError"), type: NotificationTypes.error }));
    }
  };

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <GameCenterFilters
        searchValue={search}
        handleSearch={setSearch}
        selectedActive={active}
        handleUpdateActive={setActive}
        handleApply={applyFilters}
      />
      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box">
            <ActionBtn txt={t("addMultiScope")} handleClick={openCreateModal} disabled={!permissions?.includes("create")} />
          </div>
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton position="right" disabled={!isListFetched} isLoading={!isListFetched} onClick={() => dispatch(handlePaginate(activePage))} />
          </div>
          <div className="page-list-header--box page-list-header--box-actions">
            <ToggleBtn
              handleClick={(view) => {
                localStorage.setItem("grid", view);
                setActiveView(view);
              }}
              activeValue={activeView}
              disabled={list.length === 0}
              icons={[
                { url: listIcon, value: "table" },
                { url: gridIcon, value: "grid" },
              ]}
            />
            <GuideButton onClick={() => openGuide("gamecentre")} />
            <DefaultDropdown
              onSelect={() => {}}
              placeholder={t("chooseAction")}
              options={[]}
              isSmall
              disabled
            />
          </div>
        </div>

        {!isListFetched && <><DataTableSkeleton columnsCount={9} rowsCount={12} /><PaginationSkeleton /></>}
        {isListFetched && <>
          <div className={`game-table-wrap ${activeView === "table" ? "" : "grid"}`}>
            {list.length === 0 ? <EmptyState message={t("noMultiScopesFound")} /> : (
              activeView === "table" ? <Table
                data={list}
                idField="id"
                selectedItems={selectedItems}
                onAllSelect={() => dispatch(handleSelectAll())}
                onSelect={(id) => dispatch(handleSelectItem(id))}
                onDelete={deleteItem}
                onEdit={openEditModal}
                hasEdit={permissions?.includes("update")}
                hasDelete={permissions?.includes("delete")}
                processingId={processingItemId}
                customColumns={(row) => ({
                  icon: (
                    <img
                      src={row.icon || NoImage}
                      alt=""
                      style={{
                        width: "30px",
                        height: "30px",
                        objectFit: "cover",
                        objectPosition: "center",
                        borderRadius: "10px",
                        display: "block",
                      }}
                      onError={(event) => {
                        event.currentTarget.onerror = null;
                        event.currentTarget.src = NoImage;
                      }}
                    />
                  ),
                })}
                columns={{
                  sortingColumns: {
                    id: { value: "ID", hasSorting: false }, image: { value: "Image", hasSorting: false },
                    icon: { value: "Icon", hasSorting: false }, title: { value: "Title", hasSorting: false },
                    slug: { value: "Slug", hasSorting: false }, items: { value: "Items", hasSorting: false },
                    isActive: { value: "Active", hasSorting: false },
                  },
                }}
              /> : <div className="game-grid">
                {list.map((item) => (
                  <div className="game-item big" key={item.id}>
                    <div className={`game-item-img ${item.isActive ? "active" : "no-active"}`}>
                      <img
                        src={item.image || NoImage}
                        alt={item.title}
                        className="game-item-img__image"
                        onError={(event) => {
                          event.currentTarget.onerror = null;
                          event.currentTarget.src = NoImage;
                        }}
                      />
                    </div>
                    <div className="game-item-content">
                      <div className="game-item-content--title">{item.title}</div>
                      <div className="game-item-content--actions">
                        <div
                          onClick={() => dispatch(handleSelectItem(item.id))}
                          className={`def-check-control ${selectedItems.includes(item.id) ? "checked" : ""}`}
                        />
                        <div className="provider">{item.items}</div>
                        {permissions?.includes("update") && (
                          <div className="option" onClick={() => openEditModal(item.id)}><img src={editIcon} alt="" /></div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {list.length > 0 && <Pagination
            handleLimitClick={(value) => { setLimit(value); dispatch(handlePaginate(1)); }}
            limitsArray={gameCenterLimits}
            activePage={activePage}
            limit={limit}
            totalItems={totalItems}
            totalPages={totalPages}
            handlePrevClick={() => activePage > 1 && dispatch(handlePaginate(activePage - 1))}
            handleNextClick={() => activePage < totalPages && dispatch(handlePaginate(activePage + 1))}
          />}
        </>}
      </div>
    </div>
  );
};

export default MultiScopeList;
